import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { FlexBox, Typography, Paginator, Grid } from '@kis-technologies/kis-ui';
import CustomLookupLogo from 'assets/custom_lookup.png';
import MembershipScanlogo from 'assets/barcode_scan.png';
import { CardContainer } from 'components/CardContainer';
import { Footer } from 'components/Footer';
import { TileLayout } from 'components/TileLayout';
import { ArrowPagination } from 'components/Pagination';
import { selectors as configSelectors, SearchOption, ScanMembershipCard } from 'services/configuration';
import { SelectionProps } from '../types';
import { useStyles } from '../styled';

const getOrderedOptions = (options: SearchOption[], tileOrder?: string[]): SearchOption[] => {
  let orderedOptions: SearchOption[] = [];
  const enabledOptions = [...options];
  if (tileOrder && tileOrder.length) {
    tileOrder.forEach((tile) => {
      const tileOption = enabledOptions.find((item) => item.name === tile);
      if (tileOption) {
        orderedOptions = orderedOptions.concat(enabledOptions.splice(enabledOptions.indexOf(tileOption), 1));
      }
    });
  }
  orderedOptions = orderedOptions.concat(enabledOptions);

  return orderedOptions;
};

const LookupSelection = (props: SelectionProps) => {
  const { onCardSelect, onPrev } = props;
  const { t, i18n } = useTranslation();

  const config = useSelector(configSelectors.selectConfiguration);
  const membershipLookup = get(config, 'ticketing.membershipLookup');
  const scanMembershipCard: ScanMembershipCard = get(membershipLookup, 'scanMembershipCard', {});
  const customSearchOptions: SearchOption[] = get(membershipLookup, 'customSearchOptions', []);
  const tileOrder = get(membershipLookup, 'tileOrder');
  const touchForward = get(config, 'configuration.globalSettings.touchForward.general.enabled');

  const [selectedIndex, setSelectedIndex] = useState<null | number>(null);
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const classes = useStyles({})();

  // get the enabled lookup options and set the order of them
  let enabledLookupOptions: SearchOption[] = [];
  if (customSearchOptions.length) {
    enabledLookupOptions = customSearchOptions.filter((option) => option.enabled);
  }
  if (scanMembershipCard.enabled) {
    enabledLookupOptions.push({ name: 'scanMembershipCard' });
  }
  const orderedTiles = getOrderedOptions(enabledLookupOptions, tileOrder);

  useEffect(() => {
    if (selectedOption !== null && touchForward) {
      onCardSelect(selectedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  const onCardClick = (option: SearchOption, idx: number) => {
    setSelectedOption(option);
    setSelectedIndex(idx);
  };

  return (
    <>
      <TileLayout headerText={t('membershipSearch.membershipSearch')}>
        {orderedTiles && orderedTiles.length > 1 ? (
          <Paginator
            paginationComponent={ArrowPagination}
            items={orderedTiles}
            itemsPerPage={3}
            page={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          >
            {({ items }) => (
              <Grid container className={classes.selectionCardContainer}>
                {items.map((option, idx) => {
                  const optionToShow = option && option.name;
                  switch (optionToShow) {
                    case 'scanMembershipCard':
                      return (
                        <CardContainer
                          imgSrc={MembershipScanlogo}
                          cardAction={() => onCardClick(option, idx)}
                          descText={
                            get(scanMembershipCard, `label.${i18n.language}`) ||
                            t('willCallSelection.scanCardDescription')
                          }
                          key={idx}
                          isSelected={selectedIndex === idx}
                        />
                      );
                    default:
                      return (
                        <CardContainer
                          imgSrc={CustomLookupLogo}
                          cardAction={() => onCardClick(option, idx)}
                          descText={get(option, `label.${i18n.language}`, '')}
                          key={idx}
                          isSelected={selectedIndex === idx}
                        />
                      );
                  }
                })}
              </Grid>
            )}
          </Paginator>
        ) : (
          <FlexBox sx={{ height: '600px' }} justify="center" align="center">
            <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
          </FlexBox>
        )}
      </TileLayout>
      {/* Footer */}
      <Footer
        onPrevClick={onPrev}
        nextText={touchForward ? undefined : t('commonText.next')}
        onNextClick={() => onCardSelect(selectedOption)}
        isNextDisabled={selectedIndex === null}
      />
    </>
  );
};

export default LookupSelection;
